import type { DictFragment } from '../translations';

/** Views: Portfolio / Pipeline / List pages + shared filter/dropdown/confirm UI. */
export const views: DictFragment = {
  fr: {
    // ── Portfolio ──
    'portfolio.viewDetails': 'Voir les détails',
    'portfolio.problem': 'Problème',
    'portfolio.goal': 'Objectif',
    'portfolio.valueAdded': 'Valeur ajoutée',
    'portfolio.deliverables': 'Livrables',
    'portfolio.resourcesEffort': 'Ressources et effort',
    'portfolio.effort': 'Effort',
    'portfolio.timeline': 'Échéancier',
    'portfolio.ownership': 'Responsabilité',
    'portfolio.sponsor': 'Commanditaire',
    'portfolio.pmoAccompanist': 'Accompagnant PMO',
    'portfolio.userGroup': 'Groupe d’utilisateurs',
    'portfolio.dependencies': 'Dépendances',
    'portfolio.enabledBy': 'Rendu possible par',
    'portfolio.enables': 'Rend possible',
    'portfolio.infrastructure': 'Infrastructure',
    'portfolio.needsCount': 'Nécessite : {n}',
    'portfolio.blocksCount': 'Bloque : {n}',
    'portfolio.backlog': 'En attente',
    'portfolio.deferred': 'Différé',
    'portfolio.stage': 'Étape',
    'portfolio.stageActive': 'Actives (masquer Terminé)',
    'portfolio.stageAll': 'Toutes les étapes',
    'portfolio.unassigned': 'Non assigné',
    'portfolio.noMatchTitle': 'Aucun projet correspondant',
    'portfolio.noMatchDesc': 'Aucun projet ne correspond à « {q} »',

    // ── Pipeline ──
    'pipeline.viewDetails': 'Voir les détails',
    'pipeline.problem': 'Problème',
    'pipeline.goal': 'Objectif',
    'pipeline.valueAdded': 'Valeur ajoutée',
    'pipeline.deliverables': 'Livrables',
    'pipeline.resourcesEffort': 'Ressources et effort',
    'pipeline.effort': 'Effort',
    'pipeline.timeline': 'Échéancier',
    'pipeline.ownership': 'Responsabilité',
    'pipeline.sponsor': 'Commanditaire',
    'pipeline.pmoAccompanist': 'Accompagnant PMO',
    'pipeline.userGroup': 'Groupe d’utilisateurs',
    'pipeline.dependencies': 'Dépendances',
    'pipeline.enabledBy': 'Rendu possible par',
    'pipeline.enables': 'Rend possible',
    'pipeline.infrastructure': 'Infrastructure',
    'card.health.green': 'Dans les temps',
    'card.health.orange': 'Jalon à échéance proche',
    'card.health.red': 'Jalon en retard',
    'pipeline.needsCount': 'Nécessite : {n}',
    'pipeline.blocksCount': 'Bloque : {n}',
    'pipeline.peopleInvolved': 'Personnes impliquées',
    'pipeline.anySponsor': 'n’importe quel commanditaire…',
    'pipeline.anyPerson': 'n’importe quelle personne…',
    'pipeline.showLess': 'Afficher moins',
    'pipeline.showAll': 'Tout afficher ({n})',
    'pipeline.unassigned': 'Non assigné',
    'pipeline.noMatchTitle': 'Aucun projet correspondant',
    'pipeline.noMatchDesc': 'Aucun projet ne correspond à « {q} »',

    // ── List ──
    'list.problem': 'Problème',
    'list.goal': 'Objectif',
    'list.valueAdded': 'Valeur ajoutée',
    'list.deliverables': 'Livrables',
    'list.resourcesEffort': 'Ressources et effort',
    'list.effort': 'Effort',
    'list.timeline': 'Échéancier',
    'list.ownership': 'Responsabilité',
    'list.sponsor': 'Commanditaire',
    'list.userGroup': 'Groupe d’utilisateurs',
    'list.dependencies': 'Dépendances',
    'list.enabledBy': 'Rendu possible par',
    'list.enables': 'Rend possible',
    'list.infrastructure': 'Infrastructure',
    'list.projects': 'Projets',
    'list.projectCountOne': '{n} projet',
    'list.projectCountOther': '{n} projets',
    'list.allDomains': 'Tous les domaines',
    'list.searchProjects': 'Rechercher des projets...',
    'list.noProjectsTitle': 'Aucun projet trouvé',
    'list.noProjectsDesc': 'Essayez d’ajuster vos filtres ou votre recherche.',
    'list.domain': 'Domaine',
    'list.level': 'Niveau',
    'list.deleteProjectTitle': 'Supprimer le projet',
    'list.deleteProjectMessage': 'Supprimer ce projet ? Cette action est irréversible.',

    // ── Filters (MultiSelectFilter) ──
    'filters.searchPlaceholder': 'Rechercher…',
    'filters.all': 'Tous',
    'filters.domain': 'Domaine',
    'filters.project': 'Projet',
    'filters.person': 'Personne',
    'filters.milestone': 'Jalon',
    'filters.kind': 'Type',
    'filters.clear': 'Effacer',
    'filters.reset': 'Réinitialiser les filtres',
    'filters.noMatch': 'Aucun résultat',
    'filters.selectedCount': '{n} sélectionné(s)',

    // ── Dropdowns (DropdownSelect / SearchableSelect) ──
    'dropdown.searchPlaceholder': 'Rechercher...',
    'dropdown.noMatches': 'Aucun résultat',
  },
  en: {
    // ── Portfolio ──
    'portfolio.viewDetails': 'View details',
    'portfolio.problem': 'Problem',
    'portfolio.goal': 'Goal',
    'portfolio.valueAdded': 'Value Added',
    'portfolio.deliverables': 'Deliverables',
    'portfolio.resourcesEffort': 'Resources & Effort',
    'portfolio.effort': 'Effort',
    'portfolio.timeline': 'Timeline',
    'portfolio.ownership': 'Ownership',
    'portfolio.sponsor': 'Sponsor',
    'portfolio.pmoAccompanist': 'PMO Accompanist',
    'portfolio.userGroup': 'User Group',
    'portfolio.dependencies': 'Dependencies',
    'portfolio.enabledBy': 'Enabled by',
    'portfolio.enables': 'Enables',
    'portfolio.infrastructure': 'Infrastructure',
    'portfolio.needsCount': 'Needs: {n}',
    'portfolio.blocksCount': 'Blocks: {n}',
    'portfolio.backlog': 'Backlog',
    'portfolio.deferred': 'Deferred',
    'portfolio.stage': 'Stage',
    'portfolio.stageActive': 'Active (hide Done)',
    'portfolio.stageAll': 'All stages',
    'portfolio.unassigned': 'Unassigned',
    'portfolio.noMatchTitle': 'No matching projects',
    'portfolio.noMatchDesc': 'No projects match "{q}"',

    // ── Pipeline ──
    'pipeline.viewDetails': 'View details',
    'pipeline.problem': 'Problem',
    'pipeline.goal': 'Goal',
    'pipeline.valueAdded': 'Value Added',
    'pipeline.deliverables': 'Deliverables',
    'pipeline.resourcesEffort': 'Resources & Effort',
    'pipeline.effort': 'Effort',
    'pipeline.timeline': 'Timeline',
    'pipeline.ownership': 'Ownership',
    'pipeline.sponsor': 'Sponsor',
    'pipeline.pmoAccompanist': 'PMO Accompanist',
    'pipeline.userGroup': 'User Group',
    'pipeline.dependencies': 'Dependencies',
    'pipeline.enabledBy': 'Enabled by',
    'pipeline.enables': 'Enables',
    'pipeline.infrastructure': 'Infrastructure',
    'card.health.green': 'On track',
    'card.health.orange': 'Milestone due soon',
    'card.health.red': 'Milestone overdue',
    'pipeline.needsCount': 'Needs: {n}',
    'pipeline.blocksCount': 'Blocks: {n}',
    'pipeline.peopleInvolved': 'People involved',
    'pipeline.anySponsor': 'any sponsor…',
    'pipeline.anyPerson': 'any person…',
    'pipeline.showLess': 'Show less',
    'pipeline.showAll': 'Show all ({n})',
    'pipeline.unassigned': 'Unassigned',
    'pipeline.noMatchTitle': 'No matching projects',
    'pipeline.noMatchDesc': 'No projects match "{q}"',

    // ── List ──
    'list.problem': 'Problem',
    'list.goal': 'Goal',
    'list.valueAdded': 'Value Added',
    'list.deliverables': 'Deliverables',
    'list.resourcesEffort': 'Resources & Effort',
    'list.effort': 'Effort',
    'list.timeline': 'Timeline',
    'list.ownership': 'Ownership',
    'list.sponsor': 'Sponsor',
    'list.userGroup': 'User Group',
    'list.dependencies': 'Dependencies',
    'list.enabledBy': 'Enabled by',
    'list.enables': 'Enables',
    'list.infrastructure': 'Infrastructure',
    'list.projects': 'Projects',
    'list.projectCountOne': '{n} project',
    'list.projectCountOther': '{n} projects',
    'list.allDomains': 'All domains',
    'list.searchProjects': 'Search projects...',
    'list.noProjectsTitle': 'No projects found',
    'list.noProjectsDesc': 'Try adjusting your filters or search.',
    'list.domain': 'Domain',
    'list.level': 'Level',
    'list.deleteProjectTitle': 'Delete Project',
    'list.deleteProjectMessage': 'Delete this project? This cannot be undone.',

    // ── Filters (MultiSelectFilter) ──
    'filters.searchPlaceholder': 'Search…',
    'filters.all': 'All',
    'filters.domain': 'Domain',
    'filters.project': 'Project',
    'filters.person': 'Person',
    'filters.milestone': 'Milestone',
    'filters.kind': 'Type',
    'filters.clear': 'Clear',
    'filters.reset': 'Reset filters',
    'filters.noMatch': 'No match',
    'filters.selectedCount': '{n} selected',

    // ── Dropdowns (DropdownSelect / SearchableSelect) ──
    'dropdown.searchPlaceholder': 'Search...',
    'dropdown.noMatches': 'No matches',
  },
};
